import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { animate, stagger } from "animejs";
import { MapPin, Search, Navigation, ExternalLink, Music } from "lucide-react";
import { T } from "../theme";

/**
 * EventsPage — "Lineups Near You". Public (logged in or not).
 *
 * Upcoming EDM events from the Edmtrain-backed cache. Leads with geolocated
 * "near you"; declining falls back to browsing the synced cities or all of
 * them. Every card links out through the event's unmodified Edmtrain link and
 * the page carries the required "Powered by Edmtrain" attribution — both are
 * API Terms of Use requirements, not decoration.
 *
 * Browse-only v1: RSVP/attendance is a future feature and gets one honest
 * line, no fake controls.
 */

const API = process.env.REACT_APP_API_URL || "";

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ─── Date grouping — the "when" is the hero ─────────────────────────────────

function dayKey(date) {
  return new Date(date).toDateString();
}

function dayLabel(date) {
  const d = new Date(date);
  const today = new Date();
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
  if (d.toDateString() === today.toDateString()) return "Tonight";
  if (d.toDateString() === tomorrow.toDateString()) return "Tomorrow";
  return d.toLocaleDateString("en-US", { weekday: "long" });
}

function daySub(date) {
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function groupByDay(events) {
  const groups = [];
  const index = new Map();
  for (const ev of events) {
    const key = dayKey(ev.date);
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ key, label: dayLabel(ev.date), sub: daySub(ev.date), events: [] });
    }
    groups[index.get(key)].events.push(ev);
  }
  return groups;
}

// ─── Event card — typography-led (the API provides no images) ───────────────

function EventCard({ event }) {
  const d = new Date(event.date);
  const artists = event.artists || [];
  const shown = artists.slice(0, 3).join(" · ");
  const more = artists.length - 3;

  const inner = (
    <>
      {/* Date block — biggest thing on the card */}
      <div aria-hidden style={{
        flexShrink: 0, width: 64, textAlign: "center",
        padding: "10px 0", borderRadius: 14,
        background: `${T.cyan}12`, border: `1px solid ${T.cyan}30`,
      }}>
        <div style={{ fontSize: "1.5rem", fontWeight: 800, color: T.cyan, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
          {d.getDate()}
        </div>
        <div style={{ fontSize: "0.68rem", fontWeight: 700, color: T.textStrong, textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 3 }}>
          {d.toLocaleDateString("en-US", { month: "short" })}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0, color: T.textPrimary, fontWeight: 700,
          fontSize: "1rem", lineHeight: 1.3, textWrap: "balance",
        }}>
          {event.name}
        </p>
        <p style={{ margin: "5px 0 0", color: T.textStrong, fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 5 }}>
          <MapPin size={12} aria-hidden style={{ flexShrink: 0 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {event.venue?.name || "Venue TBA"}{event.venue?.city ? ` · ${event.venue.city}` : ""}
          </span>
        </p>
        {shown && (
          <p style={{ margin: "4px 0 0", color: T.textMuted, fontSize: "0.76rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {shown}{more > 0 ? `  +${more} more` : ""}
          </p>
        )}
      </div>

      <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
        {event.ages && (
          <span style={{
            padding: "2px 9px", borderRadius: 12,
            background: "rgba(255,255,255,0.06)", border: T.border,
            color: T.textStrong, fontSize: "0.68rem", fontWeight: 700,
          }}>
            {event.ages}
          </span>
        )}
        {event.link && <ExternalLink size={14} color={T.textMuted} aria-hidden />}
      </div>
    </>
  );

  const cardStyle = {
    display: "flex", alignItems: "center", gap: 16,
    padding: "16px 18px", minHeight: 44,
    background: T.card, backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: T.border, borderRadius: T.radius,
    textDecoration: "none",
    transition: "border-color 0.18s, transform 0.18s, box-shadow 0.18s",
  };

  // ToS: each displayed event carries its unmodified Edmtrain link
  return event.link ? (
    <a
      className="event-card"
      href={event.link}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${event.name} — details on Edmtrain`}
      style={cardStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `${T.cyan}66`;
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = `0 10px 30px ${T.cyan}1a`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "";
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
      }}
    >
      {inner}
    </a>
  ) : (
    <div className="event-card" style={cardStyle}>{inner}</div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const NEAR_ME = "__near__";
const ALL = "__all__";

function EventsPage() {
  const [events, setEvents] = useState(null); // null = loading
  const [regions, setRegions] = useState([]);
  const [error, setError] = useState(false);
  const [scope, setScope] = useState(ALL);      // NEAR_ME | ALL | region label
  const [geo, setGeo] = useState(null);          // { lat, lng } once granted
  const [geoState, setGeoState] = useState("idle"); // idle | asking | ok | denied
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  const fetchEvents = useCallback(async (which, coords) => {
    setEvents(null);
    setError(false);
    try {
      let url = `${API}/api/events/upcoming`;
      if (which === NEAR_ME && coords) url += `?lat=${coords.lat}&lng=${coords.lng}`;
      else if (which !== ALL && which !== NEAR_ME) url += `?region=${encodeURIComponent(which)}`;
      const res = await axios.get(url);
      setEvents(res.data.events || []);
      if (res.data.regions?.length) setRegions(res.data.regions);
    } catch (err) {
      console.error("Events fetch error:", err);
      setError(true);
      setEvents([]);
    }
  }, []);

  // Initial load: everywhere, soonest first; geolocation upgrades to Near me
  useEffect(() => { fetchEvents(ALL); }, [fetchEvents]);

  const askNearMe = useCallback(() => {
    if (!navigator.geolocation) return;
    setGeoState("asking");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setGeo(coords);
        setGeoState("ok");
        setScope(NEAR_ME);
        setQuery("");
        fetchEvents(NEAR_ME, coords);
      },
      () => setGeoState("denied"),
      { enableHighAccuracy: false, timeout: 8000 }
    );
  }, [fetchEvents]);

  const pickScope = (which) => {
    setQuery("");
    if (which === NEAR_ME) {
      if (geo) { setScope(NEAR_ME); fetchEvents(NEAR_ME, geo); }
      else askNearMe();
      return;
    }
    setScope(which);
    fetchEvents(which);
  };

  // Debounced text search (existing endpoint); empty query returns to scope
  useEffect(() => {
    clearTimeout(searchTimer.current);
    const q = query.trim();
    if (q.length < 2) return;
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/api/events/search?q=${encodeURIComponent(q)}`);
        setEvents(res.data.events || []);
        setError(false);
      } catch (err) {
        console.error("Event search error:", err);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [query]);

  const clearSearch = () => {
    setQuery("");
    fetchEvents(scope, geo);
  };

  // Entrance: date groups cascade in
  useEffect(() => {
    if (!events?.length || prefersReducedMotion()) return;
    const cards = document.querySelectorAll(".event-card");
    if (!cards.length) return;
    cards.forEach((c) => { c.style.transition = "none"; });
    animate(cards, {
      opacity: [0, 1],
      translateY: [10, 0],
      duration: 300,
      delay: stagger(28),
      ease: "out(3)",
      onComplete: () => cards.forEach((c) => {
        c.style.transition = "";
        c.style.transform = "";
        c.style.opacity = "";
      }),
    });
  }, [events]);

  const groups = events?.length ? groupByDay(events) : [];
  const searchActive = query.trim().length >= 2;

  const chip = (active) => ({
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "9px 16px", minHeight: 40, borderRadius: 20,
    cursor: "pointer", flexShrink: 0,
    fontSize: "0.8rem", fontWeight: 700,
    background: active ? `${T.cyan}1f` : "rgba(255,255,255,0.05)",
    border: active ? `1px solid ${T.cyan}66` : T.border,
    color: active ? T.cyan : T.textStrong,
    transition: "all 0.15s",
  });

  return (
    <div style={{ minHeight: "100vh", background: T.bg, padding: "88px 16px 64px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>

        {/* ── Header ── */}
        <header style={{ textAlign: "center", marginBottom: 26 }}>
          <h1 style={{
            margin: "0 0 10px",
            fontFamily: "var(--font-display)",
            fontSize: "clamp(1.9rem, 5vw, 2.6rem)", fontWeight: 700,
            color: T.pink,
            textShadow: `0 0 26px ${T.purple}60`,
            textWrap: "balance",
          }}>
            Lineups Near You
          </h1>
          <p style={{ margin: "0 auto", maxWidth: "52ch", color: T.textStrong, fontSize: "0.95rem", lineHeight: 1.5 }}>
            Upcoming EDM shows and fests — find the floor, bring your kandi.
            RSVP with your crew is coming soon ✌️
          </p>
          <p style={{ margin: "10px 0 0" }}>
            <a
              href="https://edmtrain.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: T.textMuted, fontSize: "0.75rem", textDecoration: "none" }}
            >
              Event data powered by <span style={{ color: T.cyan, fontWeight: 700 }}>Edmtrain</span>
            </a>
          </p>
        </header>

        {/* ── Search ── */}
        <div style={{ position: "relative", marginBottom: 14 }}>
          <Search size={15} color="rgba(255,255,255,0.35)" aria-hidden style={{
            position: "absolute", left: 15, top: "50%", transform: "translateY(-50%)",
          }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search events, venues, festivals…"
            aria-label="Search events"
            style={{
              width: "100%", padding: "13px 40px",
              background: T.inputBg, border: T.inputBorder,
              borderRadius: 14, color: T.textPrimary, fontSize: "0.92rem",
              outline: "none", boxSizing: "border-box",
            }}
          />
          {query && (
            <button
              onClick={clearSearch}
              aria-label="Clear search"
              style={{
                position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                width: 36, height: 36, borderRadius: "50%", border: "none",
                background: "rgba(255,255,255,0.07)", color: T.textStrong,
                cursor: "pointer", fontSize: "0.85rem",
              }}
            >
              ✕
            </button>
          )}
        </div>

        {/* ── City switcher ── */}
        {!searchActive && (
          <div role="group" aria-label="Choose a city" style={{
            display: "flex", gap: 8, overflowX: "auto", paddingBottom: 10,
            marginBottom: 24, scrollbarWidth: "none",
          }}>
            <button onClick={() => pickScope(NEAR_ME)} aria-pressed={scope === NEAR_ME} style={chip(scope === NEAR_ME)}>
              <Navigation size={13} aria-hidden />
              {geoState === "asking" ? "Locating…" : "Near me"}
            </button>
            <button onClick={() => pickScope(ALL)} aria-pressed={scope === ALL} style={chip(scope === ALL)}>
              All cities
            </button>
            {regions.map((r) => (
              <button key={r} onClick={() => pickScope(r)} aria-pressed={scope === r} style={chip(scope === r)}>
                {r}
              </button>
            ))}
          </div>
        )}
        {geoState === "denied" && scope !== NEAR_ME && !searchActive && (
          <p style={{ margin: "-12px 0 20px", color: T.textMuted, fontSize: "0.76rem" }}>
            Location's off — no worries, pick a city above.
          </p>
        )}

        {/* ── Loading ── */}
        {(events === null || searching) && (
          <div role="status" aria-label="Loading events">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="feed-skeleton" style={{ height: 88, borderRadius: 20, marginBottom: 12 }} />
            ))}
          </div>
        )}

        {/* ── Error ── */}
        {error && events?.length === 0 && !searching && (
          <div role="alert" style={{
            textAlign: "center", padding: "52px 32px",
            background: `${T.red}0f`, border: `1px solid ${T.red}40`, borderRadius: 24,
          }}>
            <div style={{ fontSize: "2.4rem", marginBottom: 14 }}>📡</div>
            <p style={{ color: T.textPrimary, fontWeight: 700, fontSize: "1.05rem", margin: "0 0 8px" }}>
              Lost the signal
            </p>
            <p style={{ color: T.textStrong, margin: "0 0 24px", fontSize: "0.9rem" }}>
              Couldn't load the lineups — give it another go ✌️
            </p>
            <button
              onClick={() => fetchEvents(scope, geo)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "12px 28px", minHeight: 44, border: "none",
                background: "linear-gradient(135deg, #ff3cac, #b44fff)",
                color: "#fff", cursor: "pointer",
                borderRadius: 50, fontWeight: 700, fontSize: "0.9rem",
                boxShadow: "0 4px 20px rgba(180,79,255,0.35)",
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* ── Empty ── */}
        {!error && events?.length === 0 && !searching && (
          <div style={{
            textAlign: "center", padding: "52px 32px",
            background: T.card, border: T.border, borderRadius: 24,
          }}>
            <Music size={34} color="rgba(255,255,255,0.18)" aria-hidden style={{ marginBottom: 12 }} />
            <p style={{ color: T.textPrimary, fontWeight: 700, fontSize: "1.05rem", margin: "0 0 8px" }}>
              {searchActive ? "Nothing matches that" : "The scene's quiet here"}
            </p>
            <p style={{ color: T.textStrong, margin: 0, fontSize: "0.9rem" }}>
              {searchActive
                ? "Try an artist, venue, or festival name."
                : "Try another city — the next one's always coming."}
            </p>
          </div>
        )}

        {/* ── Grouped listings ── */}
        {events?.length > 0 && !searching && groups.map((g) => (
          <section key={g.key} aria-label={`${g.label} ${g.sub}`} style={{ marginBottom: 28 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "0 0 12px 4px" }}>
              <h2 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 800, color: T.textPrimary }}>
                {g.label}
              </h2>
              <span style={{ color: T.textMuted, fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                {g.sub}
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {g.events.map((ev) => <EventCard key={ev.id} event={ev} />)}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default EventsPage;
